/**
 * Lead Centre (LMC) client-side reporting — best-effort, non-blocking, never throws.
 *
 * The LMC tracker script (installed in the root layout `<head>`) exposes
 * `window.LeadCentre.submit()` once loaded; it auto-merges UTM/gclid attribution
 * from the `_lc_attr` cookie. Every lead-capture form on the site calls
 * `reportToLeadCentre()` from its OWN successful-submit path, after the existing
 * Payload submission has already succeeded — this must never affect that path.
 */

export type LeadCentreLeadType =
  | 'BUYER_ENQUIRY'
  | 'SELLER_APPRAISAL'
  | 'INSTANT_ESTIMATE'
  | 'OFF_MARKET'
  | 'RENTAL_ENQUIRY'
  | 'OTHER'

interface LeadCentreSubmitPayload {
  name: string
  email?: string
  phone?: string
  lead_type: LeadCentreLeadType
  source_url?: string
  source_form?: string
  property_id?: string
  /** The enquiry contents (message, address, budget…) — shown on the lead in LMC. */
  fields?: Record<string, unknown>
}

declare global {
  interface Window {
    LeadCentre?: {
      submit: (payload: LeadCentreSubmitPayload) => Promise<unknown>
    }
  }
}

export interface ReportToLeadCentreArgs {
  name: string
  email?: string
  phone?: string
  leadType: LeadCentreLeadType
  sourceForm?: string
  propertyId?: string
  /** Everything the person typed beyond their contact details. */
  fields?: Record<string, unknown>
}

/**
 * Fire-and-forget report to Lead Centre. Swallows every failure mode — missing
 * script, network error, misconfigured tenant key — so it can never break the
 * form UX it's called from.
 */
export function reportToLeadCentre({
  name,
  email,
  phone,
  leadType,
  sourceForm,
  propertyId,
  fields,
}: ReportToLeadCentreArgs): void {
  try {
    /* eslint-disable no-restricted-globals -- the typeof check on this line IS
       the guard; everything after it only runs in the browser */
    if (typeof window === 'undefined' || !window.LeadCentre) return
    if (!name.trim() || (!email?.trim() && !phone?.trim())) return

    void window.LeadCentre.submit({
      name: name.trim(),
      email: email?.trim() || undefined,
      phone: phone?.trim() || undefined,
      lead_type: leadType,
      source_form: sourceForm,
      property_id: propertyId,
      fields: compactFields(fields),
    }).catch(() => {
      // best-effort — LMC being unreachable must never surface to the visitor
    })
  } catch {
    // same — a thrown error here must never block the caller's own success path
  }
}

/**
 * Payload form-builder submissions all shape their data the same way —
 * `{ field, value }[]` — but the field slugs are CMS-managed per form (set by
 * whoever builds the form in /admin), so this checks the common variants
 * rather than assuming one fixed set of names.
 */
export function extractContactFromFormData(dataToSend: { field: string; value: unknown }[]): {
  name: string
  email?: string
  phone?: string
} {
  const get = (...keys: string[]): string | undefined => {
    for (const key of keys) {
      const entry = dataToSend.find((d) => d.field === key)
      const value = entry?.value != null ? String(entry.value).trim() : ''
      if (value) return value
    }
    return undefined
  }

  const fullName = get('name', 'full-name', 'fullName', 'your-name')
  const firstName = get('first-name', 'firstName', 'first_name')
  const lastName = get('last-name', 'lastName', 'last_name')
  const name = fullName || [firstName, lastName].filter(Boolean).join(' ').trim()

  const email = get('email', 'your-email', 'email-address')
  const phone = get('mobile', 'phone', 'mobile-phone', 'contact-number', 'phone-number')

  return { name, email, phone }
}

/** Field slugs that are contact details rather than enquiry content. */
const CONTACT_KEYS = new Set([
  'name', 'full-name', 'fullName', 'your-name',
  'first-name', 'firstName', 'first_name', 'last-name', 'lastName', 'last_name',
  'email', 'your-email', 'email-address',
  'mobile', 'phone', 'mobile-phone', 'contact-number', 'phone-number',
])

/** Keys that are never enquiry content (files, signatures, consent plumbing). */
const NOISE_KEYS = new Set(['signature', 'documents', 'files', 'file', 'captcha', 'token'])

/**
 * Turns a Payload form-builder submission (`{ field, value }[]`) into the
 * enquiry contents to send to Lead Centre — every answer except the contact
 * details, which LMC already stores on the contact.
 */
export function formDataToFields(dataToSend: { field: string; value: unknown }[]): Record<string, unknown> {
  const out: Record<string, unknown> = {}
  for (const { field, value } of dataToSend) {
    if (CONTACT_KEYS.has(field) || NOISE_KEYS.has(field)) continue
    out[field] = value
  }
  return out
}

/**
 * Same idea for custom (non form-builder) forms: pass the form state object
 * and the keys that hold contact details, get back only the enquiry content.
 */
export function omitContact(form: object, contactKeys: string[]): Record<string, unknown> {
  const out: Record<string, unknown> = {}
  for (const [key, value] of Object.entries(form)) {
    if (contactKeys.includes(key) || NOISE_KEYS.has(key)) continue
    out[key] = value
  }
  return out
}

/** Drops empty values so LMC only shows what was actually answered. */
function compactFields(fields?: Record<string, unknown>): Record<string, unknown> | undefined {
  if (!fields) return undefined
  const out: Record<string, unknown> = {}
  for (const [key, value] of Object.entries(fields)) {
    if (value === undefined || value === null) continue
    if (typeof value === 'string' && !value.trim()) continue
    if (Array.isArray(value) && value.length === 0) continue
    if (typeof value === 'object' && !Array.isArray(value)) continue // files, nested blobs
    out[key] = value
  }
  return Object.keys(out).length ? out : undefined
}
